import { Injectable } from '@angular/core';
import {
  BasePageMetaResolver,
  BreadcrumbMeta,
  CanonicalPageResolver,
  CategoryPageMetaResolver, CmsService, PageBreadcrumbResolver, PageDescriptionResolver,
  PageRobotsMeta,
  PageRobotsResolver, PageTitleResolver,
  ProductSearchPage, ProductSearchService,
  TranslationService
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { CustomProductSearchPage } from 'src/app/spartacus/custom/model/custom-product-search-model';
import { CustomPageLinkService } from '../../spartacus/custom/core/cms/page/routing/custom-page-link.service';

/**
 * Resolves the page data for the Product Listing Page.
 *
 * The page title, and breadcrumbs are resolved in this implementation only.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomCategoryPageMetaResolver
  extends CategoryPageMetaResolver
  implements
  PageDescriptionResolver,
  PageTitleResolver,
  PageRobotsResolver,
  CanonicalPageResolver,
  PageBreadcrumbResolver {

  constructor(
    protected productSearchService: ProductSearchService,
    protected cms: CmsService,
    protected translation: TranslationService,
    protected basePageMetaResolver: BasePageMetaResolver,
    protected pageLinkService: CustomPageLinkService
  ) {
    super(productSearchService, cms, translation, basePageMetaResolver);
  }

  resolveTitle(): Observable<string> {
    return (<Observable<ProductSearchPage>>this.searchPage$).pipe(
      filter((page: ProductSearchPage) => !!page.pagination),
      switchMap((p: ProductSearchPage) => {
        const categoryMetaTitle = p?.categoryMetaTitle;
        const catDefaultTitle = p?.breadcrumbs ? p?.breadcrumbs[0].facetValueName : '';
        const catCustomTitle = p?.title;
        let title: string;
        if (categoryMetaTitle === '$longDescription') {
          title = catDefaultTitle;
        } else if (categoryMetaTitle) {
          title = categoryMetaTitle;
        } else if (catCustomTitle) {
          title = catCustomTitle;
        } else {
          title = catDefaultTitle;
        }
        if (title) {
          return of(title);
        }
        return super.resolveTitle();
      })
    );
  }

  resolveDescription(): Observable<string> {
    return (this.searchPage$ as Observable<ProductSearchPage>).pipe(
      filter((page: ProductSearchPage) => !!page.pagination),
      switchMap((p: ProductSearchPage) => {
        const metaDescriptionCatOhgar = p?.categoryMetaDescription;
        let title: string;
        if (!metaDescriptionCatOhgar) {
          title = p.categoryDescription;
        } else if (metaDescriptionCatOhgar === '$longDescription') {
          title = p?.categoryLongDescription;
        } else {
          title = metaDescriptionCatOhgar;
        }
        title = title?.replace(/(&nbsp;)/g, '');
        title = title?.replace(/(\r\n|\n|\r)/gm, '');
        return of(title?.replace(/<\/?[^>]+(>|$)/g, '').substring(0, 150));
      })
    );
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest([
      (this.searchPage$ as Observable<CustomProductSearchPage>).pipe(),
      this.translation.translate('common.home'),
    ]).pipe(
      map(([page, label]: [CustomProductSearchPage, string]) =>
        page.showBreadcrumbs
          ? this.resolveBreadcrumbData(page as CustomProductSearchPage, label)
          : []
      )
    );
  }

  protected resolveBreadcrumbData(
    page: CustomProductSearchPage,
    label: string
  ): BreadcrumbMeta[] {
    const breadcrumbs: BreadcrumbMeta[] = [];
    breadcrumbs.push({ label, link: '/' });

    for (const br of page.showBreadcrumbs ?? []) {
      if (br.facetValueName) {
        breadcrumbs.push({
          label: br.facetValueName,
          link: `/c/${br.facetValueNormalizedName}/${br.facetValueCode}`,
        });
      }
    }
    return breadcrumbs;
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  /**
 * Resolves the canonical url for the category listing page.
 *
 * The default options will be used to resolve the url, which means that
 * all query parameters are removed and https and www are added explicitly.
 */
  resolveCanonicalUrl(): Observable<string> {
    return (<Observable<ProductSearchPage>>this.searchPage$).pipe(
      filter((page: ProductSearchPage) => !!page.pagination),
      map((p: ProductSearchPage) => {
        return this.pageLinkService.getCanonicalUrl({}, p.categoryCanonicalUrl);
      }));
  }
}
